import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Grid,
  Box,
  Divider,
  Switch,
  useMediaQuery,
  Tooltip,
  IconButton,
} from '@mui/material'
import { HelpOutlineOutlined } from '@mui/icons-material'
import React, {useState, useCallback} from 'react'
import tick from 'shared/assets/icons/tick.png'
import MainHeader from '../shared/organism/MainHeader'
import { fonts, theme} from '../../theme/DashboardTheme'
import { useAppDispatch, useAppSelector } from '../../redux/storeExports'
import DowngradeModal from '../shared/molecule/DowngradeSubscriptionModal'
import {
  selectTieredProducts,
  selectCurrentSubscription,
  selectActiveGrant,
  selectPendingOffer,
  purchaseSubscription,
  createPortalSession,
  upgradeSubscription,
  downgradeSubscription,
  selectSubscriptionSchedule,
  fetchSubscriptionSchedule
} from '../../redux/product/ProductSlice'
import { SubscriptionType } from 'shared/redux/product/ProductTypes'
import PageLayout from '../layouts/PageLayout'
import CircularProgress from '@mui/material/CircularProgress';
import {ReactComponent as ShieldIcon} from '../../assets/images/shield-tick.svg'
import { formatDate, getAdAttributes, getMetapixelAttributes, getMixpanelUserAttributes, getFirebaseUserAttributes } from '../../utils/helpers'
import SubscriptionScheduleModal from '../shared/molecule/SubscriptionScheduleModal'
import SubscriptionScheduleWarningModal from '../shared/molecule/ScheduleWarningModal'
import MobileSubscriberWarningModal from '../shared/molecule/MobileSubscriberWarning'
import { Toast } from '../toast/toast'
import Cookies from 'universal-cookie'
const cookie = new Cookies()

const styles = {
  price: {
    ...fonts.Inter.Medium,
    fontSize: {xs: '28px', sm: '25px', md: '42px', lg: '50px'},
    color: '#042115',
  },
  perMonth: {
    ...fonts.Inter.SemiBold,
    fontSize: '18px',
  },
  caption: {
    ...fonts.Inter.Bold,
    fontSize: '16px',
  },
  body: {
    display: 'flex',
    alignItems: 'flex-start',
    color: '#1A1B20',
    fontSize: '14px',
    fontWeight: 420,
  },
  marginCaption: {
    marginBottom: '18px',
  },
  icon: {
    marginTop:'8px',
    marginRight: '7px',
  },
  borderHighlight: {
    border: '2px solid #9135E0',
  },
  attachedText: {
    position: 'absolute',
    background: '#C67EFF',
    ...fonts.Inter.SemiBold,
    fontSize: '16px',
    padding: '5px 0px 5px 0px',
    textAlign: 'center',
    color: '#fff',
    top: '-1px',
    left: 0,
    right: 0,
    fontWeight: 'bold',
  },
  cardStyle : {
    minWidth: '100%',
    display: 'flex',
    flexDirection: 'column',  
    marginBottom: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8.2px',
    position: 'relative',
    height: '100%',
  },
  cardContentStyle : {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pt:10,
    pb:5,
    px: 5,
    flexGrow: 1,
  },
  button : {
    width:'100%', p: 2, mt:'18px', backgroundColor: '#9135E0', borderRadius: '8px', color: '#fff',
    minHeight: '30px',
      '&.Mui-disabled': {
          background: 'white',
          color: '#A0A0A4',
          border: '2px solid #A0A0A4',
          opacity: 0.7,
      },'&:hover':{
        backgroundColor: '#9135E0',
        color: '#fff'
      }
   },
  activeSubButton: {
    backgroundColor: '#fff',
    color: '#9135E0',
    border: '2px solid #9135E0',
    width:'100%',
    p: 2,
    mt:'18px',
    borderRadius: '8px',
    '&:hover':{
      backgroundColor: '#fff',
      color: '#9135E0',
    }, '&.Mui-disabled': {
      backgroundColor: '#fff',
      color: '#9135E0',
    }
}
} as const

const SubscriptionView = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [requestedTier, setRequestedTier] = useState<SubscriptionType>('Free')
  const [requestedPlan, setRequestedPlan] = useState<SubscriptionType>('Free')
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useAppDispatch()
  const offer = useAppSelector(selectPendingOffer)
  const activeGrant = useAppSelector(selectActiveGrant)
  const currentPlan = selectCurrentSubscription(activeGrant)
  const tieredProducts = useAppSelector(selectTieredProducts)
  const [openDowngradeModal, setOpenDowngradeModal] = useState(false)
  const [openManageScheduleModal, setOpenManageScheduleModal] = useState(false)
  const [isScheduleLoading, setIsScheduleLoading] = useState(false)
  const [scheduleWarningModalOpen,setScheduleWarningModalOpen] = useState(false)
  const subscriptionSchedule = useAppSelector(selectSubscriptionSchedule)
  const [openMobileSubscriptionWarningModal, setOpenMobileSubscriptionWarningModal] = useState(false)
  const [selectedProductType, setselectedProductType] = useState<'Individual' | 'Family'>(
    currentPlan.includes('Family')? 'Family' : 'Individual'
  )

  const visibleTiers = offer?.visible_subscription_tiers
  const isTierVisible = useCallback((tier: string) => {
    if (!visibleTiers || visibleTiers.length === 0) return true
    return visibleTiers.includes(tier)
  }, [visibleTiers])

  const fetchSchedule = useCallback(async () => {
  setIsScheduleLoading(true)
  try {
    await dispatch(fetchSubscriptionSchedule())
  } finally {
    setIsScheduleLoading(false)
  }
 }, [dispatch])

  const planIndex : Record<SubscriptionType, number> = {
    'Free': 0,
    'Core-Individual-Yearly': 1,
    'Premium-Individual-Yearly': 3,
    'Ultimate-Individual-Yearly': 5,
    'Core-Family-Yearly': 2,
    'Premium-Family-Yearly': 4,
    'Ultimate-Family-Yearly': 6,
  }

  const getTrackingAttributes = async() => {
    const mixpanelUserAttributes = await getMixpanelUserAttributes()
    const metapixelAttributes = getMetapixelAttributes()
    const adAttributes = getAdAttributes()
    const ip = cookie.get("ip")
    const agent = navigator.userAgent
    return {
      ad_data : adAttributes,
      ga4_user: getFirebaseUserAttributes(),
      mixpanel_user: mixpanelUserAttributes,
      metapixel_user: metapixelAttributes,
      customer : {...(agent ? { "user_agent": agent } : {}), ...(ip ? { "ip": ip } : {})}
    }
  }

  const getButtonText = (plan:SubscriptionType) => {
    if(isLoading && plan===requestedTier){
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress size="30px" color="secondary" thickness={4} />
        </div>
      )
    }
    if (plan==='Free' && currentPlan==='Free') {
      return 'Current Plan'
    } else if (plan!=='Free' && currentPlan==='Free') {
      return 'Get Started'
    } else if (plan === currentPlan) {
      return 'Manage Plan'
    } else if(plan!=='Free' && !plan.includes('Family') && currentPlan.includes('Family')){
      return 'Contact Support'
    }
    return "Switch Plan"
  }

  const getButtonDisableFlag = (plan:SubscriptionType) => {
    if (isLoading) return true
    if (plan==='Free') return true
    return false
  }

  const getButtonStyle = (plan:SubscriptionType) => {
    if (plan === currentPlan) return styles.activeSubButton
    return styles.button
  }

  const getCardStyle = (plan:SubscriptionType) => {
    if (plan === currentPlan) return {...styles.cardStyle, ...styles.borderHighlight}
    return styles.cardStyle
  }

  const createCheckoutSession=async (sub: SubscriptionType) => {
      const product = tieredProducts[sub]
      const adTrackingAttributes = await getTrackingAttributes()
      const session = await dispatch(
        purchaseSubscription({
          sku: product.sku,
          ext_price_id: product.price_id,
          success_url: window.location.origin + '/subscribe/success',
          cancel_url: window.location.href,
          promo_code: offer?.code || '',
          tracking_metadata: adTrackingAttributes
        }),
      ).unwrap();
      window.location.href = session.url;
  }

  const onManageSubscription = async () => {
    const response = await dispatch(createPortalSession()).unwrap()
    window.location.href = response.url
  }
  
  const onSubscriptionUpgrade = async (sub: SubscriptionType) => {
    if (!activeGrant){ 
      console.log('No active subscription found, cannot upgrade user'); 
      return
    }
    
    const product = tieredProducts[sub];
    const session = await dispatch(
      upgradeSubscription({
        current_sku: activeGrant.sku,
        upgrade_sku: product.sku,
        new_price_id: product.price_id,
        return_url: window.location.origin + '/subscription'
      }),
    ).unwrap();
    window.location.href = session.url;
  }

  const onSubscriptionDowngrade = async (sub: SubscriptionType) => {
    setOpenDowngradeModal(false)
    setIsLoading(true)
    setRequestedTier(sub)
    if (!activeGrant){ 
      console.log('No active subscription found, cannot downgrade user'); 
      return
    }
    try{
      const product = tieredProducts[sub];
      await dispatch(
        downgradeSubscription({
          current_sku: activeGrant.sku,
          downgrade_sku: product.sku,
          new_price_id: product.price_id
        }),
      )}
    catch(error){
      console.log(`error creating checkout session for ${sub} : ${error}`)
    }finally{
      setIsLoading(false)
      fetchSchedule()
    }
  }

  const onButtonClick = async(plan:SubscriptionType) =>{
    if (
      activeGrant &&
      activeGrant.payment_platform &&
      (activeGrant.payment_platform === 'android' ||
      activeGrant.payment_platform === 'ios')
    ) {
      setOpenMobileSubscriptionWarningModal(true)
    }else{
      setRequestedPlan(plan)
      if(!plan.includes('Family') && currentPlan.includes('Family')){
        window.open('https://meprism1.atlassian.net/servicedesk/customer/portal/1', '_blank')
        return
      }
      if(subscriptionSchedule && subscriptionSchedule.has_schedule){
        const scheduledType = subscriptionSchedule.schedule?.subscription_type
        const selectedPlanBase = plan.replace(/-(Monthly|Yearly)$/i, '')
        if (
          subscriptionSchedule.schedule?.schedule_type === 'downgrade' &&
          scheduledType === selectedPlanBase
        ) {
          Toast.show({
            type: 'success',
            text1: 'You already have a subscription downgrade scheduled for this plan',
          })
          return
        }
        setScheduleWarningModalOpen(true)
      }else{
        await onSubscriptionAction(plan)
      }
    }
  }

  const onSubscriptionAction = async(plan:SubscriptionType) => {
    setIsLoading(true)
    setRequestedTier(plan)
    try{
        if (currentPlan === "Free"){
          await createCheckoutSession(plan)
        } else if (plan === currentPlan) {
          await onManageSubscription()
        } else if (planIndex[plan] < planIndex[currentPlan]) {
          setOpenDowngradeModal(true)
        } else if (planIndex[plan] > planIndex[currentPlan]) {
          await onSubscriptionUpgrade(plan)
        }
    }catch(error){
      console.log(`error creating checkout session for ${plan} : ${error}`)
    }finally{
      setIsLoading(false)
      fetchSchedule()
    }
  }

  return (
     <PageLayout>
      <MainHeader title={'Subscription'} />
      <Box my={'30px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <Typography variant='h4' sx={{fontWeight: '700', fontSize:'19px', color: '#6B7280', width:'92px', lineHeight: '22px'}}>Individual</Typography>
        <Switch
          defaultChecked={currentPlan.includes("Family")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>)=>event.target.checked ? setselectedProductType('Family') : setselectedProductType('Individual')}
          sx={{
            mx:4,
            width: 70,
            height: 42,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: 0,
              top: '50%',
              left: 4,
              transform: 'translateY(-50%)',
              '&.Mui-checked': {
                transform: 'translateX(32px) translateY(-50%)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#9135E0 !important',
                    opacity: '0.85 !important',          
                },
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 30,
              height: 30,
            },
            '& .MuiSwitch-track': {
              borderRadius: 20,
              backgroundColor: '#E9E9EA',
              opacity: 1,
            },
          }}
        />
        <Typography variant='h4' sx={{fontWeight: '700', fontSize:'19px', color: '#6B7280', width:'84px', lineHeight: '22px'}}>Family</Typography>
      </Box>
      <Grid 
        container
        spacing={2}
        alignItems="stretch"
        justifyContent="center"
        sx={{
          my: isMobile? 10:0,
          minWidth: '100%',
          overflowX: 'auto',
          overflowY: 'auto',
          pb: 2
        }}
       >
        {/* Card 1 */}
        <Grid item lg={2.9} md={5.9} sm={5.9} xs={12}>  
          <Card
            sx={getCardStyle('Free')}>
            <CardContent
              sx={styles.cardContentStyle}>
              <Typography variant="h3" sx={{alignSelf: 'flex-start',color: '#3A155A', fontWeight: 'bold', fontSize: '19px'}}>
                Free
              </Typography>
              <Typography variant="h5" mt={'18px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '14px', minHeight: '40px'}}>
                Secure your personal info.
              </Typography>
              <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} width={'100%'}>
                <Typography sx={{color: '#3A155A', fontWeight: '700', fontSize: '34px'}} mr={2}>
                  $0
                </Typography>
                <Typography sx={{
                  color: '#6B7280',
                  fontWeight: '420',
                  fontSize: '14px',
                  mt: '10px'
                }}> /mo</Typography>
              </Stack>
              <Typography variant="h5" mt={'8px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '13px'}}>
                Free Forever
              </Typography>
              <Button
              variant={'contained'}
              disabled={getButtonDisableFlag('Free')}
              sx={getButtonStyle('Free')}>
                {getButtonText('Free')}
              </Button>
              <Divider sx={{ width: '100%', my: '21px' }} />
              <Stack display={'flex'} flexDirection={'column'} spacing={'13px'} px={3}>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/> Find your leaked data on
                  Google & data broker sites
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  See your results in your Privacy Dashboard
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/> Customer Support +
                  self-help resources
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        {/* Card 2 */}
        {isTierVisible('Core') && <Grid item lg={2.9} md={5.9} sm={5.9} xs={12}>
          <Card
            sx={selectedProductType==='Individual'? getCardStyle('Core-Individual-Yearly'): getCardStyle('Core-Family-Yearly')}>
            <CardContent
              sx={styles.cardContentStyle}>
              <Typography variant="h3" sx={{alignSelf: 'flex-start',color: '#3A155A', fontWeight: 'bold', fontSize: '19px'}}>
                Core
              </Typography>
              <Typography variant="h5" mt={'18px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '14px', minHeight: '40px'}}>
                Wider reach with removal tools.
              </Typography>
              <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} width={'100%'}>
                <Typography sx={{color: '#3A155A', fontWeight: '700', fontSize: '34px'}} mr={2}>
                  {selectedProductType==='Individual'? '$4.17': '$7.50'}
                </Typography>
                <Typography sx={{
                  color: '#6B7280',
                  fontWeight: '420',
                  fontSize: '14px',
                  mt: '10px'
                }}> /mo</Typography>
              </Stack>
              <Typography variant="h5" mt={'8px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '13px'}}>
                  {selectedProductType==='Individual'? '$49.99': '$89.99'} billed annually
              </Typography>
              <Button
              variant={'text'}
              onClick={()=>{
                onButtonClick(selectedProductType==='Individual'?'Core-Individual-Yearly':'Core-Family-Yearly')
              }}
              disabled={getButtonDisableFlag(selectedProductType==='Individual'?'Core-Individual-Yearly':'Core-Family-Yearly')}
              sx={selectedProductType==='Individual'? getButtonStyle('Core-Individual-Yearly'): getButtonStyle('Core-Family-Yearly')}>
                {selectedProductType==='Individual'? getButtonText('Core-Individual-Yearly'): getButtonText('Core-Family-Yearly')}
              </Button>
              <Divider sx={{ width: '100%', my: '21px' }} />
              <Stack display={'flex'} flexDirection={'column'} spacing={4} px={3}>
                {selectedProductType==='Family' &&
                  <Typography sx={styles.body}>
                    <img src={tick} style={styles.icon}/> Covers two people
                </Typography>
                }
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/> Removal from Google & 100+ brokers
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Fully automated removals                 
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Persistent scanning every 30 days 
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                    Alerts when data reappears
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Privacy Dashboard + reports
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Priority customer support  
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>}
        {/* Card 3 */}
        {isTierVisible('Premium') && <Grid item lg={2.9} md={5.9} sm={5.9} xs={12}>
          <Card
            sx={selectedProductType==='Individual'? getCardStyle('Premium-Individual-Yearly'): getCardStyle('Premium-Family-Yearly')}>
            <CardContent
              sx={styles.cardContentStyle}>
              <Typography variant="h3" sx={{alignSelf: 'flex-start',color: '#3A155A', fontWeight: 'bold', fontSize: '19px'}}>
                Premium
              </Typography>
              <Typography variant="h5" mt={'18px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '14px', minHeight: '40px'}}>
                Perfect for executives and public figures.
              </Typography>
              <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} width={'100%'}>
                <Typography sx={{color: '#3A155A', fontWeight: '700', fontSize: '34px'}} mr={2}>
                  {selectedProductType==='Individual'? '$14.99': '$19.17'}
                </Typography>
                <Typography sx={{
                  color: '#6B7280',
                  fontWeight: '420',
                  fontSize: '14px',
                  mt: '10px'
                }}> /mo</Typography>
              </Stack>
              <Typography variant="h5" mt={'8px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '13px'}}>
                  {selectedProductType==='Individual'? '$179.99': '$229.99'} billed annually
              </Typography>
              <Button
              variant={'text'}
              onClick={()=>{
                onButtonClick(selectedProductType==='Individual'?'Premium-Individual-Yearly':'Premium-Family-Yearly')
              }}
              disabled={getButtonDisableFlag(selectedProductType==='Individual'?'Premium-Individual-Yearly':'Premium-Family-Yearly')}
              sx={selectedProductType==='Individual'? getButtonStyle('Premium-Individual-Yearly'): getButtonStyle('Premium-Family-Yearly')}>
                {selectedProductType==='Individual'? getButtonText('Premium-Individual-Yearly'): getButtonText('Premium-Family-Yearly')}
              </Button>
              <Divider sx={{ width: '100%', my: '21px' }} />
              <Stack display={'flex'} flexDirection={'column'} spacing={4} px={3}>
                {selectedProductType==='Family' &&
                  <Typography sx={styles.body}>
                    <img src={tick} style={styles.icon}/> Covers two people
                </Typography>
                }
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/> Removal from Google & 180+ brokers
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Fully automated removals                 
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Persistent scanning every 30 days 
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                    Alerts when data reappears
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Privacy Dashboard + reports
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Priority customer support  
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>}
        {/* Card 4 */}
        {isTierVisible('Ultimate') && <Grid item lg={2.9} md={5.9} sm={5.9} xs={12}>
          <Card
            sx={selectedProductType==='Individual'? getCardStyle('Ultimate-Individual-Yearly'): getCardStyle('Ultimate-Family-Yearly')}>
            <CardContent
              sx={styles.cardContentStyle}>
              <Typography variant="h3" sx={{alignSelf: 'flex-start',color: '#3A155A', fontWeight: 'bold', fontSize: '19px'}}>
                Ultimate
              </Typography>
              <Typography variant="h5" mt={'18px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '14px', minHeight: '40px'}}>
                Highest Protection.
              </Typography>
              <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} width={'100%'}>
                <Typography sx={{color: '#3A155A', fontWeight: '700', fontSize: '34px'}} mr={2}>
                  {selectedProductType==='Individual'? '$24.99': '$29.17'}
                </Typography>
                <Typography sx={{
                  color: '#6B7280',
                  fontWeight: '420',
                  fontSize: '14px',
                  mt: '10px'
                }}> /mo</Typography>
              </Stack>
              <Typography variant="h5" mt={'8px'} sx={{alignSelf: 'flex-start',fontWeight: '420',color: '#6B7280', fontSize: '13px'}}>
                  {selectedProductType==='Individual'? '$299.99': '$349.99'} billed annually
              </Typography>
              <Button
              variant={'text'}
              onClick={()=>{
                onButtonClick(selectedProductType==='Individual'?'Ultimate-Individual-Yearly':'Ultimate-Family-Yearly')
              }}
              disabled={getButtonDisableFlag(selectedProductType==='Individual'?'Ultimate-Individual-Yearly':'Ultimate-Family-Yearly')}
              sx={selectedProductType==='Individual'? getButtonStyle('Ultimate-Individual-Yearly'): getButtonStyle('Ultimate-Family-Yearly')}>
                {selectedProductType==='Individual'? getButtonText('Ultimate-Individual-Yearly'): getButtonText('Ultimate-Family-Yearly')}
              </Button>
              <Divider sx={{ width: '100%', my: '21px' }} />
              <Stack display={'flex'} flexDirection={'column'} spacing={4} px={3}>
                {selectedProductType==='Family' &&
                  <Typography sx={styles.body}>
                    <img src={tick} style={styles.icon}/> Covers two people
                </Typography>
                }
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/> Removal from Google & 600+ brokers
                </Typography>

                <Box sx={{ gap: '0px', ...styles.body }}>
                  <img 
                    src={tick} 
                    style={{ ...styles.icon, marginTop: '8px' }}
                    alt="tick" 
                  />
                  <Typography 
                    component="div" 
                    sx={{ 
                      fontFamily: 'InterMedium',
                      fontSize: '14px',
                      lineHeight: '20px', 
                      color: '#1A1B20' 
                    }}
                  >
                    Expanded coverage for maximum data removal
                    <Tooltip
                      title="Maximize your removal coverage with our Expanded Coverage feature available only on an Ultimate Plan. 
                      Expanded coverage covers data brokers who attempt to make the optout process as difficult as possible. 
                      This method enables Priwall to send a direct opt-out signal to hundreds of data brokers"
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontFamily: 'InterRegular',
                            fontSize: '10px',
                            lineHeight: '16px',
                            fontWeight: 500,
                            color: '#ffffff',
                          },
                        },
                      }}
                    >
                      <IconButton 
                        component="span"
                        sx={{ 
                          padding: 0, 
                          marginLeft: '4px',
                          verticalAlign: 'text-bottom',
                        }}
                      >
                        <HelpOutlineOutlined
                          sx={{
                            fontSize: '16px',
                            color: 'rgba(145, 53, 224, 1)',
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Typography>
                </Box>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                    Social Media Privacy Controls
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Unlimited custom removal requests
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                    Persistent scanning every 30 days
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Alerts when data reappears
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Privacy Dashboard + reports
                </Typography>
                <Typography sx={styles.body}>
                  <img src={tick} style={styles.icon}/>
                  Priority customer support 
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>}
        {!isScheduleLoading && subscriptionSchedule && subscriptionSchedule.has_schedule && (
          <Typography variant="body2" sx={{ mt: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShieldIcon
                style={{
                  width: 24,
                  height: 24,
                  marginRight: 8,
                  marginLeft: isMobile ? 0 : 3,
                }}
              />

              <Box>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: '#6B7280',
                    fontWeight: 400,
                    fontFamily: 'InterRegular',
                    fontSize: '12px',
                    letterSpacing: '1px'
                  }}
                >
                  {subscriptionSchedule.schedule?.schedule_type === 'cancel'
                    ? 'Your subscription is scheduled to cancel'
                    : `Your plan will ${subscriptionSchedule.schedule?.schedule_type} to the ${subscriptionSchedule.schedule?.subscription_type} plan`}

                  {' at the end of your current billing cycle'}

                  {subscriptionSchedule.schedule?.schedule_start_time && (
                    <> on {formatDate(subscriptionSchedule.schedule.schedule_start_time)}.</>
                  )}
                </Typography>

                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: '#9135E0',
                    fontFamily: 'InterMedium',
                    fontWeight: 600,
                    fontSize: '12px',
                    ml: 1,
                    cursor: 'pointer',
                    letterSpacing: '1px'
                  }}
                  onClick={()=>{
                    setOpenManageScheduleModal(true)
                  }}
                >
                  Manage schedule.
                </Typography>
              </Box>
            </Box>
          </Typography>
        )}

        </Grid>
        <DowngradeModal
        open={openDowngradeModal}
        setOpen={setOpenDowngradeModal}
        onDowngrade={onSubscriptionDowngrade}
        subscription={requestedTier}
        />
        
        <SubscriptionScheduleModal
        open={openManageScheduleModal}
        setOpen={setOpenManageScheduleModal}
        onClose={fetchSchedule}
        />

        <SubscriptionScheduleWarningModal
        open={scheduleWarningModalOpen}
        setOpen={setScheduleWarningModalOpen}
        onSubscribeAction={onSubscriptionAction}
        requestedPlan={requestedPlan}
        />
        <MobileSubscriberWarningModal
          open={openMobileSubscriptionWarningModal}
          setOpen={setOpenMobileSubscriptionWarningModal}
        />
      </PageLayout>
  )
}

export default SubscriptionView
